import {
  shallowEqual, useDispatch, useSelector
} from 'react-redux';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import { Provider } from './context';
import useShakaPlayer from '@/hooks/audio/useShakaPlayer';
import useAudioElement from '@/hooks/audio/useAudioElement';
import useOnline from '@/hooks/browser/useOnline';
import {
  selectAudioError,
  selectAudioMuted,
  selectAudioPaused
} from '@/state/selectors/audio';
import {
  nextTrack,
  pauseAudio,
  playAudio,
  readyStateChanged,
  toggleCanPlayFired,
  toggleError,
  toggleMetaLoaded
} from '@/state/audio/actions';

const MAX_AUDIO_RETRIES = 8;
const AUDIO_SKIP_SEEK_AMOUNT_ON_ERROR = 0.25;

const selector = (state) => ({
  paused: selectAudioPaused(state),
  muted: selectAudioMuted(state),
  error: selectAudioError(state)
});

const AudioProvider = ({ children }) => {
  const dispatch = useDispatch();
  const {
    paused,
    muted,
    error
  } = useSelector(selector, shallowEqual);

  const { online, onlineRef } = useOnline();
  const { audioRef, audioRefReady } = useAudioElement();
  const {
    reloadAudio,
    extractBufferingTimeFromPlayer,
    getCurrentTrackStats,
    setCurrentTrackStats,
    shakaReadyForPlayback
  } = useShakaPlayer();

  // Holds the number of audio retries attempted
  const audioRetryCountRef = useRef(0);
  // Should we allow attempts to reload the audio stream?
  const audioRetryEnabledRef = useRef(true);
  // Holds the timeout for marking an audio retry as successful
  const audioRetrySuccessTimeoutRef = useRef(null);

  // Handles syncing the audio element's playback state with redux state
  const syncAudioPlayState = useCallback(() => {
    const audioElement = audioRef.current;

    if (audioElement) {
      if (paused && !audioElement.paused) {
        // If the user successively plays and pauses very quickly, the audio ref can get out of sync
        // This timeout makes sure that there is no race condition
        setTimeout(() => audioElement.pause(), 25);
      } else if (shakaReadyForPlayback && audioElement.paused) {
        audioElement.play();
      }
    }
  }, [audioRef, paused, shakaReadyForPlayback]);

  const handleResetShakaRetries = useCallback(({ markAsSuccessDelay = 0 } = {}) => {
    clearTimeout(audioRetrySuccessTimeoutRef.current);

    audioRetrySuccessTimeoutRef.current = setTimeout(
      () => { audioRetryCountRef.current = 0; },
      markAsSuccessDelay
    );
  }, []);

  const handleError = useCallback(() => {
    dispatch(pauseAudio());
    dispatch(toggleError(true));
  }, [dispatch]);

  const handleRetryAudio = useCallback(({ force = false } = {}) => {
    // If we are forcing a retry, reset retry state
    if (force) {
      handleResetShakaRetries();
      audioRetryEnabledRef.current = true;
    }

    // We only retry the audio if the flag is enabled. This avoids multiple
    // audio retries occuring simultaneously
    if (audioRetryEnabledRef.current) {
      // Disable any other audio retry as one is now in progress
      audioRetryEnabledRef.current = false;
      const attemptNumber = audioRetryCountRef.current + 1;

      if (onlineRef.current && (attemptNumber <= MAX_AUDIO_RETRIES)) {
        audioRetryCountRef.current = attemptNumber;
        // Nudge the seek. This ensures that we don't get stuck at a seek point that is corrupted
        const seekWithNudge = (audioRef.current.currentTime || 0) + AUDIO_SKIP_SEEK_AMOUNT_ON_ERROR;
        
        reloadAudio({ seek: seekWithNudge }).then(() => {
          // Once we have reloaded the audio, we can allow further retries to occur if there is another error
          audioRetryEnabledRef.current = true;
        });
      } else {
        // After failing the maximum number of retries, pause the audio and toggle the error => true in global state
        handleResetShakaRetries();
        handleError();
        audioRetryEnabledRef.current = true;
      }
    }
  }, [
    audioRef,
    reloadAudio,
    handleResetShakaRetries,
    handleError,
    onlineRef
  ]);

  // AUDIO TAG LISTENERS
  // -------------------------------
  // WITHIN THESE WE SHOULD UPDATE OUR OWN STATE BASED ON CHANGES
  // TO THE AUDIO TAG
  // -------------------------------
  const audioOnCanPlay = useCallback(() => {
    dispatch(toggleCanPlayFired(true));
  }, [dispatch]);

  const audioOnEnded = useCallback(() => {
    dispatch(nextTrack());
  }, [dispatch]);

  const audioOnPause = useCallback(() => {
    dispatch(pauseAudio());
  }, [dispatch]);

  const audioOnPlay = useCallback(() => {
    dispatch(playAudio());
  }, [dispatch]);

  const audioOnLoadedMetadata = useCallback(() => {
    dispatch(toggleMetaLoaded(true));
  }, [dispatch]);

  const audioOnPlaying = useCallback(() => {
    // Audio is now playing so any existing reload of audio was successful
    // Slightly delay resetting retry state, as sometimes a new error can occur within miliseconds (corrupt stream)
    handleResetShakaRetries({ markAsSuccessDelay: 1000 });
  }, [handleResetShakaRetries]);

  const audioOnLoadedData = useCallback(() => {
    const newReadyState = audioRef.current.readyState;

    dispatch(readyStateChanged(newReadyState));
  }, [audioRef, dispatch]);

  // REDUX STATE LISTENERS
  // -------------------------------
  // WITHIN THESE EFFECTS WE SHOULD UPDATE THE AUDIO TAG BASED ON STATE CHANGES
  // -------------------------------

  // Play/pause
  // Keep the audio element play state in sync with our own app state
  useEffect(() => syncAudioPlayState(), [syncAudioPlayState]);

  // Play/pause
  // If we failed to recover from an error state, the user needs to manually trigger playing audio
  // In this case, we should reload the audio stream to recover from the error state
  useEffect(() => {
    const audioElement = audioRef.current;

    if (!paused && error) {
      handleRetryAudio({ force: true });

      if (audioElement.paused) {
        audioElement.play();
      }
    }
  }, [audioRef, error, paused, handleRetryAudio]);

  // Set mute
  useEffect(() => {
    if (audioRefReady) {
      if (muted) {
        audioRef.current.muted = true;
      } else {
        audioRef.current.muted = false;
      }
    }
  }, [muted, audioRef, audioRefReady]);

  // SHAKA PLAYER LISTENERS
  // -------------------------------
  // WITHIN THESE WE SHOULD UPDATE OUR OWN STATE BASED ON CHANGES
  // FROM SHAKA PLAYER
  // -------------------------------
  const onPlayerBuffering = useCallback(({ buffering }) => {
    const { initialBufferTimeSeconds } = getCurrentTrackStats();

    const bufferTime = extractBufferingTimeFromPlayer();

    if (!buffering && !initialBufferTimeSeconds && bufferTime) {
      setCurrentTrackStats({ initialBufferTimeSeconds: bufferTime });
    }

    // Shaka can sometimes get caught in a buffering state even though playback is available
    syncAudioPlayState();
  }, [
    syncAudioPlayState,
    extractBufferingTimeFromPlayer,
    getCurrentTrackStats,
    setCurrentTrackStats
  ]);

  const onPlayerIdleStateIdle = useCallback(_ => _, []);

  const onPlayerStateChange = useCallback(() => _ => _, []);

  const onPlayerError = useCallback((err = {}) => {
    // See https://shaka-player-demo.appspot.com/docs/api/shaka.util.Error.html
    const {
      severity,
      category,
      code
    } = err;

    const isSevere = (severity >= 2);
    const isManifestParsingError = category === 4;
    const isLoadInterruption = (code === 7000);

    if (
      isSevere
        && !isLoadInterruption // 7000: load interruptions don't break playback so we don't need to refetch audio
        && !isManifestParsingError // 4000: manifest errors cannot be helped by reloading audio
    ) {
      if (online) {
        // We have encountered an error, so any existing retry success timeouts should be cancelled
        clearTimeout(audioRetrySuccessTimeoutRef.current);
        // If we are online, we can simply retry fetching the audio
        handleRetryAudio();
      }
    }

    if (isManifestParsingError) {
      handleError();
    }
  }, [handleRetryAudio, online, handleError]);

  // Handle chrome media sessions
  useEffect(() => {
    if (audioRefReady) {
      if ('mediaSession' in navigator) {
        // Disable the seek bar entirely
        navigator.mediaSession.setPositionState({
          duration: null,
          position: null
        });
      }
    }
  }, [audioRefReady]);

  // Attach event listeners to the audio element
  useEffect(() => {
    const au = audioRef.current;

    if (audioRefReady) {
      au.controls = true;
      au.preload = 'metadata';
      au.addEventListener('play', audioOnPlay);
      au.addEventListener('pause', audioOnPause);
      au.addEventListener('playing', audioOnPlaying);
      au.addEventListener('ended', audioOnEnded);
      au.addEventListener('loadeddata', audioOnLoadedData);
      au.addEventListener('loadedmetadata', audioOnLoadedMetadata);
      au.addEventListener('canplay', audioOnCanPlay);
    }

    return () => {
      if (audioRefReady) {
        au.removeEventListener('play', audioOnPlay);
        au.removeEventListener('pause', audioOnPause);
        au.removeEventListener('playing', audioOnPlaying);
        au.removeEventListener('ended', audioOnEnded);
        au.removeEventListener('loadeddata', audioOnLoadedData);
        au.removeEventListener('loadedmetadata', audioOnLoadedMetadata);
        au.removeEventListener('canplay', audioOnCanPlay);
      }
    };
  }, [
    audioRef,
    audioRefReady,
    audioOnPlay,
    audioOnPause,
    audioOnPlaying,
    audioOnEnded,
    audioOnLoadedMetadata,
    audioOnCanPlay,
    onPlayerError,
    audioOnLoadedData
  ]);

  const value = useMemo(() => ({
    // Handlers for shaka player
    onPlayerError,
    onPlayerIdleStateIdle,
    onPlayerBuffering,
    onPlayerStateChange,
    shakaReadyForPlayback
  }), [
    onPlayerError,
    onPlayerIdleStateIdle,
    onPlayerBuffering,
    onPlayerStateChange,
    shakaReadyForPlayback
  ]);

  return (
    <Provider value={ value }>
      { children }
    </Provider>
  );
};

AudioProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

export default AudioProvider;